import React, { memo } from 'react';
import { Position, NodeProps } from '@xyflow/react';
import BaseNode from '../BaseNode';
import { typeLabelOf } from '../../nodeCatalog';
import { useTranslation } from 'react-i18next';

interface ControlNodeData {
  label: string;
  subType?: string;
  config: {
    // 분기 노드 속성
    condition?: string;
    defaultPath?: string;
    
    // 합류 노드 속성
    joinType?: 'ALL' | 'ANY';
    
    // 사용자확인 노드 속성
    message?: string;
    timeoutSecs?: number;
    continueOnTimeout?: boolean;
  };
}

const ControlNode: React.FC<NodeProps<any>> = ({ data, selected, type, id, ...props }) => {
  const { t } = useTranslation();
  const nodeData = data as ControlNodeData;
  if (!nodeData) {
    return null;
  }

  // 노드 타입에 따라 아이콘 결정
  let icon;
  let subtitle = '';
  
  switch (type) {
    case 'branch':
      icon = (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M16 3h5v5" />
          <path d="M4 20L21 3" />
          <path d="M21 16v5h-5" />
          <path d="M15 15l6 6" />
          <path d="M4 4l5 5" />
        </svg>
      );
      subtitle = typeLabelOf('branch');
      break;
    case 'merge':
      icon = (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M8 3H3v5" />
          <path d="M3 8l5 5" />
          <path d="M21 3h-5v5" />
          <path d="M16 8l5-5" />
          <path d="M12 20v-8" />
          <path d="M15 12h-6" />
        </svg>
      );
      subtitle = typeLabelOf('merge');
      break;
    case 'userConfirm':
      icon = (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4" />
          <path d="M12 8h.01" />
        </svg>
      );
      subtitle = typeLabelOf('userConfirm');
      break;
    default:
      icon = (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 5v14" />
          <path d="M5 12h14" />
        </svg>
      );
      subtitle = t('workflow:control.default');
  }

  // 부제목에 설정 추가
  if (type === 'branch' && nodeData.config?.condition) {
    subtitle += ` - ${nodeData.config.condition.substring(0, 15)}${nodeData.config.condition.length > 15 ? '...' : ''}`;
  } else if (type === 'merge' && nodeData.config?.joinType) {
    subtitle += ` - ${nodeData.config.joinType}`;
  } else if (type === 'userConfirm' && nodeData.config?.message) {
    subtitle += ` - ${nodeData.config.message.substring(0, 15)}${nodeData.config.message.length > 15 ? '...' : ''}`;
  }

  // 분기 노드의 경우 추가 핸들(아래쪽) 설정
  const handleConfig = type === 'branch' 
    ? {
        target: true,
        source: true,
        extraHandles: [{
          id: 'false-path',
          type: 'source' as const,
          position: Position.Bottom,
          className: 'w-3 h-3 bg-red-400 border-2 border-white'
        }]
      }
    : undefined;

  return (
    <BaseNode
      id={id}
      data={data}
      type={type}
      selected={selected}
      handles={handleConfig}
      className="px-4 py-2"
      icon={
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {icon.props.children}
        </svg>
      }
      label={nodeData.label || t('workflow:node.unnamed')}
      subtitle={subtitle}
      {...props}
    />
  );
};

export default memo(ControlNode); 